import { ApiProvider } from '@/provider/ApiProvider';
import { getTabAssignment } from '@/auth/activeAssignment';

import type { Result } from './api';
import type { ClientStatusFields } from './clientStatus';
import { PERMISSION_SECURITY_RULES_MANAGE, PERMISSION_USER_EDIT } from './capabilities';
import { reasonMessage, toRefusal } from './refusals';

/**
 * Settings > Manage Users > Rules (Authorization-Plan §13.16): the security
 * rules of PNET, each disabled / notify / enforced with parameters, a live
 * "would affect N now" preview and the event history. Calls go to the
 * identity service (`/identity/api/admin/security-rules...`) through
 * ApiProvider.identityRequest. Every field of an answer is guarded: the
 * service is built in parallel and older servers answer without some of them.
 */

export const RULE_MODES = ['disabled', 'notify', 'enforced'] as const;
export type RuleMode = (typeof RULE_MODES)[number];

export const MODE_LABEL: Record<RuleMode, string> = {
  disabled: 'Disabled',
  notify: 'Notify',
  enforced: 'Enforced',
};

export type RuleParams = Record<string, number | string | boolean | null>;

export interface SecurityRule {
  key: string;
  mode: RuleMode;
  params: RuleParams;
  title: string;
  description: string;
  iso_refs: string[];
  last_run_at: string | null;
  /** free text or an object of counts (`{acted: 3, notified: 5}`); null = never run */
  last_result: unknown;
  /** the modes this rule offers (alert rules: disabled + notify); the server's list wins */
  allowed_modes: RuleMode[];
  /** false: shown greyed with `unavailable_reason`, nothing can be changed */
  available: boolean;
  unavailable_reason: string | null;
  /** Notify mode: who gets the mail (Enforced always notifies every allowed recipient) */
  notify_user: boolean;
  notify_admins: boolean;
  /** who this rule can notify: ['user', 'admins'] or ['admins'] */
  allowed_recipients: Recipient[];
}

export const RECIPIENTS = ['user', 'admins'] as const;
export type Recipient = (typeof RECIPIENTS)[number];

/** Who "admins" are, in every rule's help. */
export const ADMINS_NOTE =
  'Admins are the Super Admins, plus the organisation’s own admins for brand, right holder and agency users.';

export interface PreviewUser {
  user_id: number | null;
  name: string;
  email: string;
  reason: string;
}

export interface RulePreview {
  affected: number;
  sample: PreviewUser[];
}

export interface RuleEvent {
  id: number | string;
  rule_key: string;
  action: string;
  user_id: number | null;
  /** shown when the server sends them (not in the contract) */
  user_name?: string | null;
  user_email?: string | null;
  detail: unknown;
  created_at: string | null;
}

export interface EventPage {
  data: RuleEvent[];
  total: number;
  /** rows per page of the server (per_page when sent, else EVENTS_PER_PAGE) */
  per_page: number;
}

/** The rules of §13.16 (owner, 2026-10-05; four more approved the same day), in display order. */
export const RULE_KEYS = [
  'mfa_password_users',
  'super_admins',
  'sso_internal',
  'inactive_users',
  'privileged_review',
  'access_review',
  'admin_grant_alert',
  'failed_signin_alert',
];

interface RuleText {
  title: string;
  description: string;
  iso: string[];
  /** what each mode does for THIS rule (the §13.16 table); only the modes it offers */
  modes: Partial<Record<RuleMode, string>>;
  /** the effect named in the confirmation before switching to Enforced */
  enforcedAction?: string;
  /** what each recipient gets by mail (no `user`: admins only) */
  recipients: Partial<Record<Recipient, string>>;
}

/** Recipients of a rule when the server does not list them: those it has texts for. */
export const defaultRecipients = (key: string): Recipient[] => {
  const t = RULE_TEXT[key];
  return t ? RECIPIENTS.filter((r) => t.recipients[r]) : [...RECIPIENTS];
};

/** "The user gets …. Admins get …." for the chosen recipients. */
export function recipientsText(key: string, who: Recipient[]): string {
  const t = RULE_TEXT[key]?.recipients ?? {};
  const parts: string[] = [];
  if (who.includes('user') && t.user) parts.push(`The user gets ${t.user}.`);
  if (who.includes('admins') && t.admins) parts.push(`Admins get ${t.admins}.`);
  return parts.join(' ');
}

/** The modes of a rule when the server does not list them: those it has texts for. */
export const defaultModes = (key: string): RuleMode[] => {
  const t = RULE_TEXT[key];
  return t ? RULE_MODES.filter((m) => t.modes[m]) : [...RULE_MODES];
};

/**
 * Texts per rule. The server's title / description / iso_refs win when sent;
 * the mode explanations are the UI's (§13.16 table).
 */
export const RULE_TEXT: Record<string, RuleText> = {
  mfa_password_users: {
    title: 'MFA for all password users',
    description: 'Everyone who signs in with a password also needs a second factor.',
    iso: ['A.5.17', 'A.8.5'],
    modes: {
      disabled: 'Nothing happens. Password users may sign in without MFA.',
      notify: 'Password users without MFA are notified; nothing is switched on.',
      enforced:
        'E-mail MFA is switched on for password users without MFA: their next sign-in needs a code.',
    },
    enforcedAction: 'e-mail MFA is switched on for them; their next sign-in needs a code',
    recipients: {
      user: 'an e-mail asking them to turn on two-step sign-in',
      admins: 'a list of password users without MFA',
    },
  },
  // replaces break_glass (owner 2026-10-15: break-glass removed, Super Admin replaces it)
  super_admins: {
    title: 'Super Admins',
    description:
      'How many Super Admins exist (Application Admin role with the platform-owner flag). Fewer than two, or more than the maximum, is flagged.',
    iso: ['A.5.18', 'A.8.2'],
    modes: {
      disabled: 'Nothing happens. Super Admins are not counted and nobody is told about new ones.',
      notify:
        'The Super Admins are counted: a warning below two or above the maximum, and a mail on every new Super Admin.',
      enforced:
        'The same mails; nothing is switched off (a Super Admin is never removed by a rule).',
    },
    recipients: {
      user: 'a mail that they are now a Super Admin',
      admins: 'a mail on every new Super Admin and when there are too few or too many',
    },
  },
  sso_internal: {
    title: 'SSO for all internal users',
    description: 'PartnershipNet staff sign in with the company single sign-on only.',
    iso: ['A.5.16', 'A.5.17', 'A.8.5'],
    modes: {
      disabled: 'Nothing happens. Internal users may still sign in with a password.',
      notify: 'Internal users who signed in with a password are notified; the sign-in still works.',
      enforced:
        'Internal users (company domains) get no PNET session without SSO; a password sign-in is refused.',
    },
    enforcedAction: 'they can no longer sign in with a password, only with SSO',
    recipients: {
      user: 'a mail asking them to sign in with Microsoft (SSO)',
      admins: 'a list of internal users who signed in with a password',
    },
  },
  inactive_users: {
    title: 'Disable inactive users',
    description: 'Accounts that have not signed in for a long time are switched off.',
    iso: ['A.5.18', 'A.8.2'],
    modes: {
      disabled: 'Nothing happens. Inactive accounts stay active.',
      notify: 'Users close to the limit are notified; nobody is disabled.',
      enforced:
        'Users close to the limit get a warning; accounts without a sign-in for the set number of days are then disabled and signed out everywhere.',
    },
    enforcedAction: 'their accounts are disabled and signed out everywhere',
    // shown on the Notify line only ("The user gets …. Admins get …."); Enforced
    // warns before disabling, said in its own mode text (owner, 2026-10-07)
    recipients: {
      user: 'a reminder to sign in',
      admins: 'a summary',
    },
  },
  privileged_review: {
    title: 'Privileged access review',
    description: 'Privileged roles are reviewed and do not run without an end date.',
    iso: ['A.5.15', 'A.5.18', 'A.8.2'],
    modes: {
      disabled: 'Nothing happens. Privileged roles may run without an end date.',
      notify: 'Privileged roles about to end, and those without an end date, are notified.',
      enforced:
        'Privileged roles cannot be saved without an end date of at most the set number of months.',
    },
    enforcedAction:
      'their privileged roles without a (short enough) end date cannot be saved again without one',
    recipients: {
      user: 'a reminder before the privileged role ends',
      admins: 'the reminder and a weekly list of privileged roles without an end date',
    },
  },
  access_review: {
    title: 'Quarterly access review',
    description: 'Organisations regularly confirm who has which role.',
    iso: ['A.5.15', 'A.5.18'],
    modes: {
      disabled: 'Nothing happens. No access reviews are requested.',
      notify: 'A review is requested every interval; nothing is switched off.',
      enforced: 'Roles that are not confirmed in time are switched off.',
    },
    enforcedAction: 'their roles are switched off when a review is not confirmed in time',
    recipients: {
      admins: 'a review request with their organisation’s users and roles to confirm',
    },
  },
  admin_grant_alert: {
    title: 'Alert on admin rights',
    description: 'Super Admins hear about every new user or role administrator.',
    iso: ['A.5.18', 'A.8.2'],
    modes: {
      disabled: 'Nothing happens.',
      notify: 'Every new user or role administrator is notified.',
    },
    recipients: {
      user: 'a mail that they now have admin rights',
      admins: 'an alert when someone gains user or role management rights',
    },
  },
  failed_signin_alert: {
    title: 'Repeated failed sign-ins',
    description: 'Many failed sign-ins on one account in a short time raise an alert.',
    iso: ['A.8.5', 'A.8.16'],
    modes: {
      disabled: 'Nothing happens.',
      notify:
        'Accounts with the set number of failed sign-ins within the time window are notified.',
    },
    recipients: {
      user: 'a mail that someone tried to sign in to their account',
      admins: 'an alert',
    },
  },
};

/** Parameters with limits (inactive_users: days 30-365, default 90; warn_days 1-60, default 14). */
export interface ParamSpec {
  name: string;
  label: string;
  min: number;
  max: number;
  default: number;
  unit: string;
}

export const RULE_PARAMS: Record<string, ParamSpec[]> = {
  // access-levels.md section 8: max_super_admins 2-50, default 5
  super_admins: [
    {
      name: 'max_super_admins',
      label: 'Warn above',
      min: 2,
      max: 50,
      default: 5,
      unit: 'Super Admins',
    },
  ],
  inactive_users: [
    {
      name: 'days',
      label: 'Disable after',
      min: 30,
      max: 365,
      default: 90,
      unit: 'days without sign-in',
    },
    {
      name: 'warn_days',
      label: 'Warn',
      min: 1,
      max: 60,
      default: 14,
      unit: 'days before',
    },
  ],
  privileged_review: [
    {
      name: 'max_months',
      label: 'End date at most',
      min: 1,
      max: 24,
      default: 12,
      unit: 'months ahead',
    },
    {
      name: 'remind_days',
      label: 'Remind',
      min: 7,
      max: 90,
      default: 30,
      unit: 'days before the end',
    },
  ],
  access_review: [
    {
      name: 'interval_days',
      label: 'Review every',
      min: 30,
      max: 365,
      default: 90,
      unit: 'days',
    },
    {
      name: 'grace_days',
      label: 'Time to confirm',
      min: 7,
      max: 60,
      default: 30,
      unit: 'days',
    },
  ],
  failed_signin_alert: [
    {
      name: 'threshold',
      label: 'Alert after',
      min: 3,
      max: 100,
      default: 10,
      unit: 'failed sign-ins',
    },
    {
      name: 'window_minutes',
      label: 'Within',
      min: 5,
      max: 1440,
      default: 60,
      unit: 'minutes',
    },
  ],
};

/** Plain-language error per parameter; empty when all are valid. */
export function validateParams(key: string, params: RuleParams): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const spec of RULE_PARAMS[key] ?? []) {
    const raw = params[spec.name];
    const n = typeof raw === 'number' ? raw : Number(String(raw ?? '').trim());
    if (raw === null || raw === undefined || String(raw).trim() === '' || !Number.isFinite(n))
      errors[spec.name] = 'Enter a number.';
    else if (!Number.isInteger(n)) errors[spec.name] = 'Enter a whole number.';
    else if (n < spec.min || n > spec.max)
      errors[spec.name] = `Between ${spec.min} and ${spec.max}.`;
  }
  if (key === 'inactive_users' && !errors.days && !errors.warn_days) {
    if (Number(params.warn_days) >= Number(params.days))
      errors.warn_days = 'The warning must come before the account is disabled.';
  }
  return errors;
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

/** Parameters with their defaults filled in, numbers as numbers. */
export function paramsWithDefaults(key: string, raw: unknown): RuleParams {
  const params: RuleParams = isRecord(raw) ? { ...(raw as RuleParams) } : {};
  for (const spec of RULE_PARAMS[key] ?? []) {
    const v = params[spec.name];
    if (v === null || v === undefined || v === '') params[spec.name] = spec.default;
    else if (typeof v === 'string' && Number.isFinite(Number(v))) params[spec.name] = Number(v);
  }
  return params;
}

export function toRule(raw: unknown): SecurityRule | null {
  if (!isRecord(raw)) return null;
  const key = str(raw.key).trim();
  if (!key) return null;
  const text = RULE_TEXT[key];
  const mode = RULE_MODES.includes(raw.mode as RuleMode) ? (raw.mode as RuleMode) : 'disabled';
  const iso = Array.isArray(raw.iso_refs)
    ? raw.iso_refs.map(String).filter(Boolean)
    : typeof raw.iso_refs === 'string' && raw.iso_refs.trim() !== ''
      ? raw.iso_refs.split(',').map((s) => s.trim())
      : (text?.iso ?? []);
  const allowed = Array.isArray(raw.allowed_modes)
    ? RULE_MODES.filter((m) => (raw.allowed_modes as unknown[]).includes(m))
    : [];
  const available = !(raw.available === false || raw.available === 0);
  const recipients = Array.isArray(raw.allowed_recipients)
    ? RECIPIENTS.filter((r) => (raw.allowed_recipients as unknown[]).includes(r))
    : [];
  const allowedRecipients = recipients.length > 0 ? recipients : defaultRecipients(key);
  /** absent (older server): every allowed recipient */
  const flag = (v: unknown, r: Recipient): boolean =>
    allowedRecipients.includes(r) && (v === undefined || v === null ? true : v === true || v === 1);
  const reason = str(raw.unavailable_reason).trim();
  return {
    key,
    mode,
    allowed_modes: allowed.length > 0 ? allowed : defaultModes(key),
    available,
    // a reason code (e.g. rule_unavailable) in words; free text as it is
    unavailable_reason: available ? null : reason ? reasonMessage(reason) : null,
    notify_user: flag(raw.notify_user, 'user'),
    notify_admins: flag(raw.notify_admins, 'admins'),
    allowed_recipients: allowedRecipients,
    params: paramsWithDefaults(key, raw.params),
    title: str(raw.title).trim() || text?.title || key,
    description: str(raw.description).trim() || text?.description || '',
    iso_refs: iso,
    last_run_at: raw.last_run_at ? str(raw.last_run_at) : null,
    last_result: raw.last_result ?? null,
  };
}

/** Known rules first in the §13.16 order, then anything new the server adds. */
const byOrder = (a: SecurityRule, b: SecurityRule) => {
  const ia = RULE_KEYS.indexOf(a.key);
  const ib = RULE_KEYS.indexOf(b.key);
  return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.key.localeCompare(b.key);
};

/** Bare arrays and `{rules: [...]}` / `{data: [...]}` are accepted as well as `{success, data}`. */
function listFrom(payload: unknown, names: string[]): unknown[] | null {
  if (Array.isArray(payload)) return payload;
  if (!isRecord(payload)) return null;
  for (const n of names) if (Array.isArray(payload[n])) return payload[n] as unknown[];
  return null;
}

const unwrapAny = (payload: unknown): Result<unknown> => {
  if (Array.isArray(payload)) return { ok: true, data: payload, message: '' };
  if (isRecord(payload) && payload.success === undefined && !('code' in payload))
    return { ok: true, data: payload, message: '' };
  const refusal = toRefusal(payload);
  if (refusal) return { ok: false, refusal };
  const body = payload as { data?: unknown; message?: unknown; total?: unknown };
  // {success, data: [...], total}: keep the total next to the rows
  const data =
    Array.isArray(body.data) && body.total !== undefined
      ? { data: body.data, total: body.total }
      : (body.data ?? null);
  return { ok: true, data, message: str(body.message) };
};

export async function listSecurityRules(): Promise<Result<SecurityRule[]>> {
  const r = unwrapAny(await ApiProvider.identityRequest('GET', 'admin/security-rules'));
  if (!r.ok) return r;
  const rows = listFrom(r.data, ['rules', 'data']) ?? [];
  const rules = rows.map(toRule).filter((x): x is SecurityRule => x !== null);
  rules.sort(byOrder);
  return { ok: true, data: rules, message: r.message };
}

export async function updateSecurityRule(
  key: string,
  body: { mode: RuleMode; params: RuleParams; notify_user: boolean; notify_admins: boolean },
): Promise<Result<SecurityRule | null>> {
  const r = unwrapAny(
    await ApiProvider.identityRequest(
      'PUT',
      `admin/security-rules/${encodeURIComponent(key)}`,
      body as unknown as Record<string, unknown>,
    ),
  );
  if (!r.ok) return r;
  return { ok: true, data: toRule(r.data), message: r.message };
}

export async function previewSecurityRule(key: string): Promise<Result<RulePreview>> {
  const r = unwrapAny(
    await ApiProvider.identityRequest(
      'GET',
      `admin/security-rules/${encodeURIComponent(key)}/preview`,
    ),
  );
  if (!r.ok) return r;
  const d = isRecord(r.data) ? r.data : {};
  const sample = (Array.isArray(d.sample) ? d.sample : []).filter(isRecord).map((s) => ({
    user_id: s.user_id === null || s.user_id === undefined ? null : Number(s.user_id),
    name: str(s.name),
    email: str(s.email),
    reason: str(s.reason),
  }));
  const affected = Number(d.affected);
  return {
    ok: true,
    data: { affected: Number.isFinite(affected) ? affected : sample.length, sample },
    message: r.message,
  };
}

export const EVENTS_PER_PAGE = 25;

export async function listSecurityEvents(q: {
  key?: string;
  page?: number;
}): Promise<Result<EventPage>> {
  const params = new URLSearchParams();
  if (q.key) params.set('key', q.key);
  params.set('page', String(q.page ?? 1));
  const r = unwrapAny(
    await ApiProvider.identityRequest('GET', `admin/security-rules/events?${params.toString()}`),
  );
  if (!r.ok) return r;
  // {data: [...], total} (bare or inside {success, data}); a bare list without a total
  const rows = listFrom(r.data, ['data', 'events']) ?? [];
  const totalRaw = isRecord(r.data) ? Number(r.data.total) : NaN;
  const perPageRaw = isRecord(r.data) ? Number(r.data.per_page) : NaN;
  const data: RuleEvent[] = rows.filter(isRecord).map((e, i) => ({
    id: (e.id as number | string | undefined) ?? `row-${i}`,
    rule_key: str(e.rule_key),
    action: str(e.action),
    user_id: e.user_id === null || e.user_id === undefined ? null : Number(e.user_id),
    user_name: e.user_name ? str(e.user_name) : null,
    user_email: e.user_email ? str(e.user_email) : null,
    detail: e.detail ?? null,
    created_at: e.created_at ? str(e.created_at) : null,
  }));
  return {
    ok: true,
    data: {
      data,
      total: Number.isFinite(totalRaw) ? totalRaw : data.length,
      per_page: Number.isInteger(perPageRaw) && perPageRaw > 0 ? perPageRaw : EVENTS_PER_PAGE,
    },
    message: r.message,
  };
}

/** POST admin/security-rules/run (security_rules_manage, Internal): one rule, or all when no key. */
export async function runSecurityRules(key?: string): Promise<Result<unknown>> {
  return unwrapAny(
    await ApiProvider.identityRequest('POST', 'admin/security-rules/run', key ? { key } : {}),
  );
}

// ---- who receives the e-mails (GET admin/security-rules/{key}/recipients) ----------------------

export interface RecipientPerson {
  id: number;
  name: string;
  email: string;
}

export interface RuleRecipients {
  user: { applies: boolean; text: string };
  platform_owners: RecipientPerson[];
  rules_managers: RecipientPerson[];
  organisation_admins: { applies: boolean; text: string; organisations: number; people: number };
  total_admins: number;
  /** the saved rule's mode and recipients (when the server sends them) */
  mode: RuleMode | null;
  notify_user: boolean | null;
  notify_admins: boolean | null;
}

const personList = (v: unknown): RecipientPerson[] =>
  (Array.isArray(v) ? v : []).filter(isRecord).map((p) => {
    const email = str(p.email).trim();
    return { id: Number(p.id), name: str(p.name).trim() || email, email };
  });

const count = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : 0;
};

const flagOrNull = (v: unknown): boolean | null =>
  v === true || v === 1 ? true : v === false || v === 0 ? false : null;

export function toRuleRecipients(raw: unknown): RuleRecipients {
  const d = isRecord(raw) ? raw : {};
  const user = isRecord(d.user) ? d.user : {};
  const admins = isRecord(d.admins) ? d.admins : {};
  const org = isRecord(admins.organisation_admins) ? admins.organisation_admins : {};
  const owners = personList(admins.platform_owners);
  const managers = personList(admins.rules_managers);
  const total = Number(d.total_admins);
  return {
    user: { applies: user.applies === true, text: str(user.text).trim() },
    platform_owners: owners,
    rules_managers: managers,
    organisation_admins: {
      applies: org.applies === true,
      text: str(org.text).trim(),
      organisations: count(org.organisations),
      people: count(org.people),
    },
    total_admins: Number.isFinite(total)
      ? total
      : owners.length + managers.length + count(org.people),
    mode: RULE_MODES.includes(d.mode as RuleMode) ? (d.mode as RuleMode) : null,
    notify_user: flagOrNull(d.notify_user),
    notify_admins: flagOrNull(d.notify_admins),
  };
}

/** GET admin/security-rules/{key}/recipients (same permission as the page). */
export async function getRuleRecipients(key: string): Promise<Result<RuleRecipients>> {
  const r = unwrapAny(
    await ApiProvider.identityRequest(
      'GET',
      `admin/security-rules/${encodeURIComponent(key)}/recipients`,
    ),
  );
  if (!r.ok) return r;
  return { ok: true, data: toRuleRecipients(r.data), message: r.message };
}

// ---- access reviews (security rule access_review) ---------------------------------------------

export interface AccessReview {
  id: number;
  context_id: number | null;
  context_type: string | null;
  organisation: string;
  requested_at: string | null;
  due_at: string | null;
  /** client status of the organisation (owner decision 2026-10-07); absent: not sent */
  organisation_status?: ClientStatusFields | null;
}

export function toAccessReview(raw: unknown): AccessReview | null {
  if (!isRecord(raw)) return null;
  const id = Number(raw.id);
  if (!Number.isInteger(id) || id <= 0) return null;
  const ctx = isRecord(raw.context) ? raw.context : {};
  const ctxId = Number(raw.context_id ?? ctx.id);
  const org = raw.organisation;
  const name =
    (isRecord(org) ? str(org.name) : str(org)).trim() ||
    str(raw.organisation_name ?? raw.context_name ?? ctx.name).trim();
  return {
    id,
    context_id: Number.isInteger(ctxId) && ctxId > 0 ? ctxId : null,
    context_type: str(raw.context_type ?? ctx.type).trim() || null,
    organisation: name || (Number.isInteger(ctxId) && ctxId > 0 ? `#${ctxId}` : '—'),
    requested_at: str(raw.requested_at ?? raw.created_at).trim() || null,
    due_at: str(raw.due_at ?? raw.due_date ?? raw.due).trim() || null,
    ...(raw.organisation_status === undefined
      ? {}
      : {
          organisation_status: isRecord(raw.organisation_status)
            ? (raw.organisation_status as ClientStatusFields)
            : null,
        }),
  };
}

/** GET admin/access-reviews?status=open */
export async function listOpenAccessReviews(): Promise<Result<AccessReview[]>> {
  const r = unwrapAny(await ApiProvider.identityRequest('GET', 'admin/access-reviews?status=open'));
  if (!r.ok) return r;
  const rows = listFrom(r.data, ['reviews', 'data']) ?? [];
  return {
    ok: true,
    data: rows.map(toAccessReview).filter((x): x is AccessReview => x !== null),
    message: r.message,
  };
}

export interface ReviewUserRole {
  role_assignment_id: number;
  role: string;
  valid_from: string | null;
  valid_until: string | null;
  /** a membership of a person from another organisation (tenant administration) */
  external?: boolean;
  /** its open 6-monthly review: confirm by then on the Memberships page */
  external_review_due_at?: string | null;
}

export interface ReviewUser {
  user_id: number;
  name: string;
  email: string;
  roles: ReviewUserRole[];
}

export interface AccessReviewDetail extends AccessReview {
  completed_at: string | null;
  users: ReviewUser[];
}

export function toReviewUser(raw: unknown): ReviewUser | null {
  if (!isRecord(raw)) return null;
  const id = Number(raw.user_id);
  if (!Number.isInteger(id) || id <= 0) return null;
  const email = str(raw.email).trim();
  return {
    user_id: id,
    name: str(raw.name).trim() || email || `#${id}`,
    email,
    roles: (Array.isArray(raw.roles) ? raw.roles : []).filter(isRecord).map((r) => ({
      role_assignment_id: Number(r.role_assignment_id),
      role: str(r.role).trim() || '—',
      valid_from: str(r.valid_from).trim() || null,
      valid_until: str(r.valid_until).trim() || null,
      ...(r.external === true
        ? {
            external: true,
            external_review_due_at: str(r.external_review_due_at).trim() || null,
          }
        : {}),
    })),
  };
}

/** GET admin/access-reviews/{id}: the review with its organisation's users and their roles. */
export async function getAccessReview(id: number): Promise<Result<AccessReviewDetail>> {
  const r = unwrapAny(await ApiProvider.identityRequest('GET', `admin/access-reviews/${id}`));
  if (!r.ok) return r;
  const review = toAccessReview(r.data);
  if (!review)
    return {
      ok: false,
      refusal: {
        status: 404,
        reason: 'access_review_not_found',
        message: reasonMessage('access_review_not_found'),
        fields: {},
        extra: {},
      },
    };
  const d = r.data as Record<string, unknown>;
  return {
    ok: true,
    data: {
      ...review,
      completed_at: str(d.completed_at).trim() || null,
      users: (Array.isArray(d.users) ? d.users : [])
        .map(toReviewUser)
        .filter((x): x is ReviewUser => x !== null),
    },
    message: r.message,
  };
}

export const ACCESS_REVIEW_PATH = '/access-review';

/**
 * Settings > Manage Users > Access review (owner 2026-10-07): an
 * organisation admin (user_edit in the active BRAND / RIGHT_HOLDER / AGENCY
 * role) reviews their own organisation; a security rules manager every
 * organisation. From the active assignment and the permissions map, no call.
 */
export function accessReviewLink(): boolean {
  const a = getTabAssignment();
  const type = a?.contextType;
  if (type === 'INTERNAL') return Boolean(ApiProvider.permission(PERMISSION_SECURITY_RULES_MANAGE));
  return (
    (type === 'BRAND' || type === 'RIGHT_HOLDER' || type === 'AGENCY') &&
    Boolean(ApiProvider.permission(PERMISSION_USER_EDIT))
  );
}

/** POST admin/access-reviews/{id}/complete: the organisation's users and roles are confirmed. */
export async function completeAccessReview(id: number): Promise<Result<unknown>> {
  return unwrapAny(
    await ApiProvider.identityRequest('POST', `admin/access-reviews/${id}/complete`, {}),
  );
}

/** Past its due time (UTC; a date alone counts until the end of that day). */
export const isOverdue = (due: string | null, now = new Date()): boolean => {
  if (!due) return false;
  const d = new Date(
    /[zZ]|[+-]\d\d:?\d\d$/.test(due)
      ? due
      : `${due.replace(' ', 'T')}${due.length <= 10 ? 'T23:59:59' : ''}Z`,
  );
  return !Number.isNaN(d.getTime()) && d.getTime() < now.getTime();
};

// ---- texts ------------------------------------------------------------------------------------

const ACTION_WORD: Record<string, string> = {
  mode_changed: 'Mode changed',
  params_changed: 'Settings changed',
  notified: 'Mail sent',
  notify: 'Mail sent',
  warned: 'Warning sent',
  enforced: 'Enforced',
  mfa_enabled: 'MFA switched on',
  disabled: 'User disabled',
  user_disabled: 'User disabled',
  approval_requested: 'Approval requested',
  approved: 'Approved',
  refused: 'Sign-in refused',
  run: 'Run',
  run_started: 'Run started',
  run_finished: 'Run finished',
};

/**
 * Event keys that are not rules (never in the rules list) but appear in the
 * event history: `organisation_require_sso` is written by the identity
 * service when a customer organisation's "Require SSO" refuses a password
 * session (action `enforced`, detail {context_id, organisation, reason, where?}).
 * The events endpoint filters on them like on a rule key.
 */
export const ORGANISATION_REQUIRE_SSO = 'organisation_require_sso';

export const EVENT_ONLY_KEYS: Record<string, string> = {
  [ORGANISATION_REQUIRE_SSO]: 'Require SSO (organisation)',
};

/** Per event key: the action words that differ from ACTION_WORD. */
const KEY_ACTION_WORD: Record<string, Record<string, string>> = {
  [ORGANISATION_REQUIRE_SSO]: { enforced: 'Sign-in refused' },
};

/** Why an organisation's Require SSO refused a session, in words. */
const SSO_REFUSAL_REASON: Record<string, string> = {
  session_refused: 'Password sign-in refused',
};

/** "mode_changed" -> "Mode changed"; unknown codes made readable. `ruleKey`: the event's key. */
export function actionText(action: string, ruleKey?: string): string {
  const own = ruleKey ? KEY_ACTION_WORD[ruleKey]?.[action] : undefined;
  if (own) return own;
  if (ACTION_WORD[action]) return ACTION_WORD[action];
  const words = action.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '—';
}

const scalar = (v: unknown): string =>
  v === null || v === undefined
    ? '—'
    : typeof v === 'object'
      ? JSON.stringify(v)
      : String(v).replace(/_/g, ' ');

/** A result or detail as one line: text as is, an object as "key: value · key: value". */
export function detailText(v: unknown): string {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'string') {
    const t = v.trim();
    if (t.startsWith('{') || t.startsWith('[')) {
      try {
        return detailText(JSON.parse(t));
      } catch {
        return t;
      }
    }
    return t;
  }
  if (Array.isArray(v)) return v.map(detailText).join(', ');
  if (isRecord(v))
    return Object.entries(v)
      .map(([k, x]) => `${k.replace(/_/g, ' ')}: ${scalar(x)}`)
      .join(' · ');
  return String(v);
}

/**
 * The detail of an event as one line. organisation_require_sso: "Organisation:
 * Adidas · Password sign-in refused" (plus where, when sent); every other key
 * as detailText.
 */
export function eventDetailText(e: Pick<RuleEvent, 'rule_key' | 'detail'>): string {
  if (e.rule_key !== ORGANISATION_REQUIRE_SSO) return detailText(e.detail);
  let d: unknown = e.detail;
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d);
    } catch {
      return detailText(d);
    }
  }
  if (!isRecord(d)) return detailText(d);
  const org = d.organisation ?? d.organisation_name;
  const orgName = (isRecord(org) ? str(org.name) : str(org ?? '')).trim();
  const ctxId = Number(d.context_id);
  const name = orgName || (Number.isInteger(ctxId) && ctxId > 0 ? `#${ctxId}` : '');
  const reasonCode = str(d.reason ?? '').trim();
  const reason = reasonCode
    ? (SSO_REFUSAL_REASON[reasonCode] ?? reasonCode.replace(/_/g, ' '))
    : 'Password sign-in refused';
  const where = str(d.where ?? '').trim();
  return [
    name ? `Organisation: ${name}` : '',
    reason,
    where ? `where: ${where.replace(/_/g, ' ')}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

/** "2026-10-05T08:00:00Z" / "2026-10-05 08:00:00" -> "2026-10-05 08:00 UTC". */
export function timeText(s: string | null | undefined): string {
  if (!s) return '';
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s.replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return s;
  return `${d.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

export const usersText = (n: number): string =>
  `${n.toLocaleString('en')} user${n === 1 ? '' : 's'}`;

// ---- who sees the page ------------------------------------------------------------------------

/**
 * The sidebar link (Settings > Manage Users > Rules), decided at once: an
 * INTERNAL active role with security_rules_manage (the permissions map holds
 * the active role's permissions; only the Application Admin role has it,
 * owner decision 2026-10-07). Being a platform owner does not count. The page
 * itself re-checks, and so does the server.
 */
export function securityRulesLink(): boolean {
  const a = getTabAssignment();
  return (
    a?.contextType === 'INTERNAL' &&
    Boolean(ApiProvider.permission(PERMISSION_SECURITY_RULES_MANAGE))
  );
}
